import React, { useState } from 'react';
import { impactData } from '../data/mockData';
import { Camera, Sparkles } from 'lucide-react';

const pulseColors = [
  'bg-pink-300/40',
  'bg-blue-300/40',
  'bg-teal-300/40',
  'bg-amber-300/40',
  'bg-purple-300/40',
  'bg-cyan-300/40',
];

const DesktopFloatingImage = ({ item, index }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const pulseColor = pulseColors[index % pulseColors.length];
  const rotation = [-3, 2, -2, 3, -1, 2, -2, 1, -3][index % 9];
  
  return (
    <div
      className="relative transition-all duration-300 cursor-pointer focus-within:z-50"
      style={{
        transform: `rotate(${rotation}deg)`,
        zIndex: isHovered ? 50 : 10,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className={`absolute -inset-2 ${pulseColor} rounded-xl blur-lg animate-pulse`}
        style={{ animationDelay: `${index * 0.3}s`, animationDuration: `${2 + index % 2}s` }}
        aria-hidden="true"
      />
      
      <div 
        className={`relative bg-white p-1.5 rounded-lg shadow-lg transition-all duration-300 w-36 h-44 ${
          isHovered ? 'scale-110 shadow-xl !rotate-0' : ''
        }`}
      >
        {!isLoaded && (
          <div className="absolute inset-1 bg-gradient-to-br from-stone-100 to-stone-200 rounded animate-pulse" aria-hidden="true" />
        )}
        
        <img
          src={item.image}
          alt={item.alt || `Moment ${index + 1} - Photo from Naomi's journey`}
          className={`w-full h-full object-cover rounded transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsLoaded(true)}
        />
        
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-white rounded-b-lg" aria-hidden="true" />
      </div>
      
      {isHovered && (
        <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-amber-400 animate-pulse" aria-hidden="true" />
      )}
    </div>
  );
};

const MobileGridImage = ({ item, index }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const pulseColor = pulseColors[index % pulseColors.length];
  const rotation = ((index * 3) % 6) - 3;
  
  return (
    <div className="relative">
      <div 
        className={`absolute -inset-1 ${pulseColor} rounded-lg blur-md animate-pulse`}
        style={{ animationDelay: `${index * 0.2}s`, animationDuration: `${2 + index % 2}s` }}
        aria-hidden="true"
      />
      
      <div 
        className="relative bg-white p-0.5 rounded-md shadow-md transition-all duration-300 w-full aspect-[4/5]"
        style={{ transform: `rotate(${rotation}deg)` }}
      >
        {!isLoaded && (
          <div className="absolute inset-0.5 bg-gradient-to-br from-stone-100 to-stone-200 rounded animate-pulse" aria-hidden="true" />
        )}
        
        <img
          src={item.image}
          alt={item.alt || `Moment ${index + 1} - Photo from Naomi's journey`}
          className={`w-full h-full object-cover rounded-sm transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsLoaded(true)}
        />
        
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-white rounded-b-md" aria-hidden="true" />
      </div>
    </div>
  );
};

const Moments = () => {
  const { title, images } = impactData;

  return (
    <section className="py-6 md:py-10 bg-gradient-to-b from-white to-stone-50 relative overflow-hidden" aria-labelledby="moments-heading">
      {/* Header */}
      <div className="text-center mb-2 md:mb-3 relative z-20">
        <Camera className="w-4 h-4 md:w-5 md:h-5 text-stone-500 mx-auto mb-1" aria-hidden="true" />
        <h2 id="moments-heading" className="text-lg md:text-xl lg:text-2xl font-bold text-stone-800">
          {title}
        </h2>
      </div>
      
      {/* Mobile grid */}
      <div className="md:hidden px-4" role="list" aria-label="Photo gallery">
        <div className="grid grid-cols-3 gap-2 max-w-sm mx-auto">
          {images.slice(0, 9).map((item, index) => (
            <div key={item.id} role="listitem">
              <MobileGridImage item={item} index={index} />
            </div>
          ))}
        </div>
      </div>
      
      {/* Desktop grid */}
      <div className="hidden md:block px-4" role="list" aria-label="Photo gallery">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-center gap-6 mb-6">
            {images.slice(0, 5).map((item, index) => (
              <div key={item.id} role="listitem">
                <DesktopFloatingImage item={item} index={index} />
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-6">
            {images.slice(5, 9).map((item, index) => (
              <div key={item.id} role="listitem">
                <DesktopFloatingImage item={item} index={index + 5} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Moments;
